import { useContext, useEffect, useState } from 'react'

import dayjs from 'dayjs'

import {
  ORDER_HISTORY_SEARCH_CONDITION_CLEAR,
  convertConditionToOrderHistoryVars,
  useOrderHistoryListApi,
  useOrderHistorySearchCondition,
  useOrderItemRankingsAnalysisMutation,
  useOrderItemRankingsApi
} from '@/common/api/modules/orderHistory'
import { OrderItemRankingPeriod } from '@/common/api/params/orderHistory'
import { isSameSearchCondition, useFirstLoading } from '@/common/api/search'
import { currencyText } from '@/common/app/currency'
import { useSiteId } from '@/common/app/site'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import AiButton from '@/components/view/atoms/button/AiButton'
import Button from '@/components/view/atoms/button/Button'
import GroupButton from '@/components/view/atoms/button/GroupButton'
import Heading from '@/components/view/atoms/heading/Heading'
import Icon, { IconType } from '@/components/view/atoms/icon/Icon'
import LoadingObject, { LoadingObjectType } from '@/components/view/atoms/loading/LoadingObject'
import Tab from '@/components/view/atoms/tab/Tab'
import TabList from '@/components/view/atoms/tab/TabList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import AiInsideArea from '@/components/view/layouts/area/AiInsideArea'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageListLoading from '@/components/view/molecules/loading/PageListLoading'
import PageListSearching from '@/components/view/molecules/loading/PageListSearching'
import MetricItem from '@/components/view/molecules/metric/MetricItem'
import NoResultGuidance from '@/components/view/molecules/search/NoResultGuidance'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import AppTitleBadge from '@/components/container/layout/AppTitleBadge'
import OrderCsvUploadModalProvider, {
  OrderCsvUploadModalContext
} from '@/components/container/modal/csv/OrderCsvUploadModalProvider'
import OrderHistoryByOrderListTable from '@/components/container/modules/recommend/orderHistory/table/OrderHistoryByOrderListTable'
import OrderHistoryListTable from '@/components/container/modules/recommend/orderHistory/table/OrderHistoryListTable'
import OrderHistorySearchConditionField from '@/components/container/modules/recommend/orderHistory/table/OrderHistorySearchConditionField'
import TitlePageInfo from '@/components/container/utils/pager/TitlePageInfo'

const PERIOD_OPTIONS = [
  { value: 'daily', label: '日別' },
  { value: 'weekly', label: '週別' },
  { value: 'monthly', label: '月別' }
]

type ActiveTab = 'orders' | 'orders_by_order' | 'rankings'

const RankChange = ({ change }: { change: number | null }) => {
  if (change === null || change === undefined) {
    return (
      <Badge badgeType={BadgeType.DEFAULT} size={10}>
        NEW
      </Badge>
    )
  }
  if (change > 0)
    return (
      <ItemAlign gapX={2} align='center'>
        <Text color={TextColor.SUCCESS} size={12}>
          {change}UP
        </Text>
        <Icon type={IconType.INCREASE} size={16} color={TextColor.SUCCESS} />
      </ItemAlign>
    )
  if (change < 0)
    return (
      <ItemAlign gapX={2} align='center'>
        <Text color={TextColor.CRITICAL} size={12}>
          {Math.abs(change)}DOWN
        </Text>
        <Icon type={IconType.DECREASE} size={16} color={TextColor.CRITICAL} />
      </ItemAlign>
    )
  return (
    <ItemAlign gapX={2} align='center'>
      <Text color={TextColor.SECONDARY} size={12}>
        STAY
      </Text>
      <Icon type={IconType.ARROW_FORWARD} size={16} color={TextColor.SECONDARY} />
    </ItemAlign>
  )
}

const RankingCard = ({
  ranking,
  rankPeriod
}: {
  ranking: OrderItemRankingPeriod
  rankPeriod: string
}) => {
  return (
    <Area>
      <Heading>
        {rankPeriod === 'daily'
          ? `${dayjs(ranking.period_start).format('YYYY年MM月DD日')}`
          : `${dayjs(ranking.period_start).format('YYYY年MM月DD日')}〜${dayjs(ranking.period_end).format('YYYY年MM月DD日')}`}
      </Heading>
      <div className='overflow-x-auto'>
        {ranking.items.length === 0 ? (
          <AreaContent>
            <Text color={TextColor.SECONDARY} align='center'>
              データなし
            </Text>
          </AreaContent>
        ) : (
          <div className='flex'>
            {ranking.items.map((item, index) => (
              <div className='border-primary flex w-52 shrink-0 flex-col gap-2 border-r p-4'>
                <ItemAlign gapX={2}>
                  <Icon type={IconType.CROWN} size={12} />
                  <Text color={TextColor.CAUTION} size={16} weight='bold'>
                    {item.rank}
                  </Text>
                  <div className='ml-11'>
                    <RankChange change={item.rank_change} />
                  </div>
                </ItemAlign>
                <Text size={12}>{item.item_id}</Text>
                <ItemAlign>
                  <MetricItem label='販売数' value={item.total_qty.toLocaleString()} />
                  <MetricItem label='受注数' value={item.order_count.toLocaleString()} />
                </ItemAlign>
                <ItemAlign gapX={2}>
                  <Badge badgeType={BadgeType.SQUARE_CRITICAL} size={12}>
                    売上
                  </Badge>
                  <Text weight='bold'>{currencyText(item.total_amount)}</Text>
                </ItemAlign>
              </div>
            ))}
          </div>
        )}
      </div>
    </Area>
  )
}

const CsvUploadOperationButton = ({
  siteId,

  onClose
}: {
  siteId: number
  onClose: () => void
}) => {
  const { showModal: showUploadModal } = useContext(OrderCsvUploadModalContext)

  return (
    <Button onClick={() => showUploadModal({ siteId, onClose })} size='large'>
      CSVを取り込む
    </Button>
  )
}

const OrderHistoryPage = () => {
  const siteId = useSiteId()
  const [activeTab, setActiveTab] = useState<ActiveTab>('orders')
  const [rankPeriod, setRankPeriod] = useState('daily')
  const [analysisText, setAnalysisText] = useState<string | null>(null)

  const [condition, setCondition, onUpdatePage] = useOrderHistorySearchCondition()
  const isDefault = isSameSearchCondition(condition, ORDER_HISTORY_SEARCH_CONDITION_CLEAR)

  const {
    data: ordersData,
    isLoading: isOrdersLoading,
    mutate
  } = useOrderHistoryListApi(
    activeTab === 'orders' || activeTab === 'orders_by_order'
      ? convertConditionToOrderHistoryVars(siteId, condition)
      : null
  )
  const isFirstLoading = useFirstLoading(isOrdersLoading)

  const { data: rankingsData, isLoading: rankingsLoading } = useOrderItemRankingsApi(
    activeTab === 'rankings' ? { site_id: siteId, period: rankPeriod, limit: 20, periods: 3 } : null
  )

  const { trigger: triggerAnalysis, isMutating: isAnalyzing } =
    useOrderItemRankingsAnalysisMutation({
      onSuccess: (data) => setAnalysisText(data.analysis),
      onError: () => setAnalysisText('分析の取得に失敗しました')
    })

  useEffect(() => {
    setAnalysisText(null)
  }, [rankPeriod])

  return (
    <OrderCsvUploadModalProvider>
      <Main type={MainLayoutType.FULL}>
        <PageTitle
          title='受注履歴'
          badgeNode={<AppTitleBadge type='recommend' />}
          description='トラッキングコード経由および、CSV取込で蓄積した受注履歴を表示します'
          behindNode={
            (activeTab === 'orders' || activeTab === 'orders_by_order') && ordersData ? (
              <TitlePageInfo
                page={condition.page}
                perPage={condition.perPage}
                total={ordersData.total}
                unit='件'
              />
            ) : undefined
          }
          rightNode={
            <>
              {activeTab === 'rankings' ? (
                <AiButton
                  onClick={() =>
                    triggerAnalysis({
                      site_id: siteId,
                      rankings: rankingsData ? rankingsData.rankings : [],
                      period_type: rankPeriod
                    })
                  }
                  loading={isAnalyzing}
                  disabled={!rankingsData}
                  size='large'
                >
                  AI解析を実行する
                </AiButton>
              ) : (
                <CsvUploadOperationButton
                  siteId={siteId}
                  onClose={() => {
                    mutate()
                    setActiveTab('orders')
                  }}
                />
              )}
            </>
          }
        />

        <Content>
          <Area noBorder>
            <TabList border>
              <Tab selected={activeTab === 'orders'} onClick={() => setActiveTab('orders')}>
                受注履歴一覧
              </Tab>
              <Tab
                selected={activeTab === 'orders_by_order'}
                onClick={() => setActiveTab('orders_by_order')}
              >
                受注明細一覧
              </Tab>
              <Tab selected={activeTab === 'rankings'} onClick={() => setActiveTab('rankings')}>
                受注商品ランキング
              </Tab>
            </TabList>
          </Area>

          {(activeTab === 'orders' || activeTab === 'orders_by_order') && (
            <>
              {isFirstLoading ? (
                <PageListLoading />
              ) : (
                <Area tableWrap noBorder>
                  <OrderHistorySearchConditionField
                    condition={condition}
                    setCondition={setCondition}
                  />
                  {isOrdersLoading ? (
                    <PageListSearching />
                  ) : ordersData?.total ? (
                    activeTab === 'orders' ? (
                      <OrderHistoryListTable
                        siteId={siteId}
                        orders={ordersData.orders}
                        total={ordersData.total}
                        condition={condition}
                        setCondition={setCondition}
                        onUpdatePage={onUpdatePage}
                        reload={mutate}
                      />
                    ) : (
                      <OrderHistoryByOrderListTable
                        orders={ordersData.orders}
                        total={ordersData.total}
                        condition={condition}
                        setCondition={setCondition}
                        onUpdatePage={onUpdatePage}
                      />
                    )
                  ) : (
                    <NoResultGuidance
                      searched={!isDefault}
                      onClickReset={
                        !isDefault
                          ? () => setCondition(ORDER_HISTORY_SEARCH_CONDITION_CLEAR)
                          : undefined
                      }
                    />
                  )}
                </Area>
              )}
            </>
          )}
          {activeTab === 'rankings' && (
            <>
              <div>
                <GroupButton
                  choices={PERIOD_OPTIONS.map((p) => ({ value: p.value, label: p.label }))}
                  value={rankPeriod}
                  onSelect={(val) => setRankPeriod(val)}
                />
              </div>
              {rankingsLoading ? (
                <PageListLoading />
              ) : rankingsData?.rankings && rankingsData.rankings.length > 0 ? (
                <>
                  {(isAnalyzing || analysisText) && (
                    <div className='flex flex-col gap-2'>
                      <Text size={16} lineHeight={150} weight='bold' tag='h2'>
                        AI ランキング分析
                      </Text>
                      <AiInsideArea border>
                        {isAnalyzing && (
                          <div className='flex flex-col gap-4 p-4'>
                            <LoadingObject type={LoadingObjectType.TEXT} />
                            <LoadingObject type={LoadingObjectType.TEXT} />
                            <LoadingObject type={LoadingObjectType.TEXT} />
                            <LoadingObject type={LoadingObjectType.TEXT} />
                            <LoadingObject type={LoadingObjectType.TEXT} />
                            <LoadingObject type={LoadingObjectType.TEXT} />
                            <LoadingObject type={LoadingObjectType.TEXT} />
                            <LoadingObject type={LoadingObjectType.TEXT} />
                          </div>
                        )}
                        {analysisText && (
                          <AreaContent>
                            {analysisText.split('\n').map((line, i) => (
                              <Text
                                key={i}
                                size={14}
                                weight={line.startsWith('■') ? 'bold' : 'normal'}
                                tag='p'
                              >
                                {line}
                              </Text>
                            ))}
                          </AreaContent>
                        )}
                      </AiInsideArea>
                    </div>
                  )}
                  {rankingsData.rankings.map((ranking, idx) => (
                    <RankingCard key={idx} ranking={ranking} rankPeriod={rankPeriod} />
                  ))}
                </>
              ) : (
                <Area noBorder>
                  <div className='py-10 text-center'>
                    <Text color={TextColor.SECONDARY}>
                      ランキングデータがありません。受注履歴データが蓄積されると、商品別の販売ランキングが表示されます。
                    </Text>
                  </div>
                </Area>
              )}
            </>
          )}
        </Content>
      </Main>
    </OrderCsvUploadModalProvider>
  )
}

export default OrderHistoryPage
